import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BetItem } from './BettingSlipDrawer';
import { getLiveEvents, getPreEvents, getSports, placeBet, getMe, getWallet, getBetHistory, BASE_URL } from '../api/api';
import { EventSnapshot, Market, Outcome, SportItem, UserOut, WalletOut, TicketOut } from '../api/types';
import MatchDetailPage from './MatchDetailPage';
import DesktopMyBetsPage from './DesktopMyBetsPage';
import DesktopAccountPage from './DesktopAccountPage';

// ── Helpers ────────────────────────────────────────────────────────────────

const SPORT_EMOJI: Record<string, string> = {
  Soccer: '⚽', Basketball: '🏀', Tennis: '🎾', 'Ice Hockey': '🏒',
  Handball: '🤾', Volleyball: '🏐', Baseball: '⚾', 'American Football': '🏈',
  'Table Tennis': '🏓', eSoccer: '🎮', eBasketball: '🎮',
};

const COUNTRY_FLAGS: Record<string, string> = {
  'Deutschland': '🇩🇪',
  'Spanien': '🇪🇸',
  'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  'Türkei': '🇹🇷',
  'Italien': '🇮🇹',
  'Frankreich': '🇫🇷',
  'Portugal': '🇵🇹',
  'Niederlande': '🇳🇱',
  'Österreich': '🇦🇹',
  'Schweiz': '🇨🇭',
  'Belgien': '🇧🇪',
  'Brasilien': '🇧🇷',
  'Argentinien': '🇦🇷',
  'USA': '🇺🇸',
  'Polen': '🇵🇱',
  'Griechenland': '🇬🇷',
  'Dänemark': '🇩🇰',
  'Schottland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
};

const TOP_COMPETITIONS = [
  { name: 'Champions League', flag: '🏆' },
  { name: 'Premier League', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { name: 'Bundesliga', flag: '🇩🇪' },
  { name: 'La Liga', flag: '🇪🇸' },
  { name: 'Serie A', flag: '🇮🇹' },
  { name: 'Ligue 1', flag: '🇫🇷' },
];

function find1x2(markets: Market[] | undefined) {
  const m = markets?.find((mk) => mk.market_id === '1' && mk.status !== -1);
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1');
  const draw = m.outcomes.find((o) => o.outcome_id === '2' || o.name.toLowerCase() === 'draw' || o.name.toLowerCase() === 'x');
  const away = m.outcomes.find((o) => o.outcome_id === '3');
  if (home && draw && away) return { home, draw, away, market: m };
  return null;
}

function findOU(markets: Market[] | undefined) {
  const m = markets?.find((mk) => mk.market_id === '18' && mk.status !== -1);
  if (!m) return null;
  const over = m.outcomes.find((o) => o.name.toLowerCase().includes('over') || o.name.startsWith('Über'));
  const under = m.outcomes.find((o) => o.name.toLowerCase().includes('under') || o.name.startsWith('Unter'));
  if (!over || !under) return null;
  return { market: m, over, under };
}

function formatOdds(val: string | number): string {
  const n = parseFloat(String(val));
  if (isNaN(n) || n <= 1) return '-';
  return n.toFixed(2).replace('.', ',');
}

function formatClock(ev: EventSnapshot): string {
  if (ev.status?.clock) return ev.status.clock.split(':')[0] + "'";
  return 'Live';
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function isWithinHours(ev: EventSnapshot, hours: number): boolean {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return false;
  const d = new Date(starts).getTime();
  const now = Date.now();
  return d >= now && d <= now + hours * 3600_000;
}

// ── Props ──────────────────────────────────────────────────────────────────

interface DesktopHomePageProps {
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onClearAll: () => void;
  onLoginClick: () => void;
  onRegisterClick: () => void;
  activePage: string;
  onNavigate: (id: string) => void;
}

// ── Component ──────────────────────────────────────────────────────────────

const DesktopHomePage: React.FC<DesktopHomePageProps> = ({
  bets, onAddBet, onRemoveBet, onClearAll,
  onLoginClick, onRegisterClick, activePage, onNavigate,
}) => {
  const [liveEvents, setLiveEvents] = useState<EventSnapshot[]>([]);
  const [preEvents, setPreEvents] = useState<EventSnapshot[]>([]);
  const [sports, setSports] = useState<SportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('Soccer');
  const [sortBy, setSortBy] = useState<'zeit' | 'wettbewerb'>('zeit');
  const [timeFilter, setTimeFilter] = useState<'all' | 'heute' | '3std' | '48std'>('all');
  const [stake, setStake] = useState('5');
  const [placing, setPlacing] = useState(false);
  const [betStatus, setBetStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [betError, setBetError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventSnapshot | null>(null);

  // Layout states for drop-downs
  const [topMarket1, setTopMarket1] = useState('Tipp');
  const [topMarket2, setTopMarket2] = useState('Über/Unter');
  const [lastMinMarket1, setLastMinMarket1] = useState('Tipp');
  const [lastMinMarket2, setLastMinMarket2] = useState('Über/Unter');
  const [liveMarket1, setLiveMarket1] = useState('Tipp');
  const [liveMarket2, setLiveMarket2] = useState('Tipp Restzeit');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [rightSidebarTab, setRightSidebarTab] = useState<'wettschein' | 'offene'>('wettschein');

  // Nested sidebar states
  const [expandedSport, setExpandedSport] = useState<string | null>('Soccer');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [selectedTournaments, setSelectedTournaments] = useState<Set<string>>(new Set());

  const toggleTournament = (t: string) => {
    setSelectedTournaments(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  const toggleCountry = (tournaments: Record<string, number>) => {
    const allT = Object.keys(tournaments);
    const allSelected = allT.every(t => selectedTournaments.has(t));
    setSelectedTournaments(prev => {
      const next = new Set(prev);
      if (allSelected) { allT.forEach(t => next.delete(t)); }
      else { allT.forEach(t => next.add(t)); }
      return next;
    });
  };

  const MARKET_OPTIONS = ['Tipp', 'Über/Unter', 'Doppelte Chance', 'Beide Teams treffen'];

  const [user, setUser] = useState<UserOut | null>(null);
  const [wallet, setWallet] = useState<WalletOut | null>(null);
  const [tickets, setTickets] = useState<TicketOut[]>([]);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLogged, setIsLogged] = useState(!!localStorage.getItem('access_token'));

  useEffect(() => {
    const handleAuthChanged = () => {
      const token = !!localStorage.getItem('access_token');
      setIsLogged(token);
      if (!token) {
        setUser(null);
        setWallet(null);
      }
    };
    window.addEventListener('auth-changed', handleAuthChanged);
    window.addEventListener('storage', handleAuthChanged);
    // Initial fetch if logged in on mount
    handleAuthChanged();
    return () => {
      window.removeEventListener('auth-changed', handleAuthChanged);
      window.removeEventListener('storage', handleAuthChanged);
    }
  }, []);

  useEffect(() => {
    if (isLogged) {
      getMe().then(setUser).catch(console.error);
      getWallet().then(setWallet).catch(console.error);
      getBetHistory().then(setTickets).catch(console.error);
    } else {
      setTickets([]);
    }
  }, [isLogged]);


  const loadData = useCallback(async () => {
    try {
      const [live, pre, sp] = await Promise.all([
        getLiveEvents(),
        getPreEvents({ hours: 48 }),
        getSports(),
      ]);
      setLiveEvents(live.items);
      setPreEvents(pre.items);
      setSports(sp.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    
    // Set up SSE for real-time streaming
    const liveStream = new EventSource(`${BASE_URL}/v1/catalog/stream/live`);
    const preStream = new EventSource(`${BASE_URL}/v1/catalog/stream/pre`);

    const handleSnapshot = (setEvents: React.Dispatch<React.SetStateAction<EventSnapshot[]>>) => (ev: MessageEvent) => {
      try {
        const snapshot: EventSnapshot = JSON.parse(ev.data);
        setEvents((prev) => {
          const idx = prev.findIndex((p) => p.event_id === snapshot.event_id);
          if (idx > -1) {
            const next = [...prev];
            next[idx] = snapshot;
            return next;
          }
          return [...prev, snapshot];
        });
      } catch (err) {
        console.error('Failed to parse SSE snapshot', err);
      }
    };

    liveStream.addEventListener('snapshot', handleSnapshot(setLiveEvents));
    preStream.addEventListener('snapshot', handleSnapshot(setPreEvents));

    return () => {
      liveStream.close();
      preStream.close();
    };
  }, [loadData]);

  // Sport tabs — top 5 by live count
  const sportCounts: Record<string, number> = {};
  for (const ev of liveEvents) {
    const s = ev.event.sport_name;
    sportCounts[s] = (sportCounts[s] || 0) + 1;
  }
  const sportTabs = Object.entries(sportCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count, emoji: SPORT_EMOJI[name] || '🏅' }));

  // Nested tree for sidebar: Sport -> Country -> Tournament -> Count
  const sportsTree = useMemo(() => {
    const tree: Record<string, Record<string, Record<string, number>>> = {};
    preEvents.forEach((ev) => {
      const s = ev.event.sport_name;
      const c = ev.event.category_name;
      const t = ev.event.tournament_name;
      if (!tree[s]) tree[s] = {};
      if (!tree[s][c]) tree[s][c] = {};
      tree[s][c][t] = (tree[s][c][t] || 0) + 1;
    });
    return tree;
  }, [preEvents]);

  // All live sport tabs (for live page)
  const allLiveSportTabs = Object.entries(sportCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count, emoji: SPORT_EMOJI[name] || '🏅' }));

  // Events for center column
  const filteredLive = liveEvents.filter((ev) => ev.event.sport_name === activeSport);
  const filteredPre = (() => {
    let base = preEvents.filter((ev) => ev.event.sport_name === activeSport);
    if (selectedTournaments.size > 0) {
      base = base.filter((ev) => selectedTournaments.has(ev.event.tournament_name));
    }
    if (timeFilter === 'heute') return base.filter((e) => isWithinHours(e, 24));
    if (timeFilter === '3std') return base.filter((e) => isWithinHours(e, 3));
    if (timeFilter === '48std') return base;
    return base.slice(0, 60);
  })();

  const isSelected = (eventId: string, outcomeId: string) =>
    bets.some((b) => b.id === `desk-${eventId}-${outcomeId}`);

  const handleOddsClick = (ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => {
    const id = `desk-${ev.event_id}-${outcome.outcome_id}`;
    if (isSelected(ev.event_id, outcome.outcome_id)) {
      onRemoveBet(id);
    } else {
      const existing = bets.find((b) => b.id.startsWith(`desk-${ev.event_id}`));
      if (existing) onRemoveBet(existing.id);
      onAddBet({
        id,
        matchId: ev.event_id,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: outcome.outcome_id === '1' ? 'home' : outcome.outcome_id === '3' ? 'away' : 'draw',
        tippLabel,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        specifiers: market.specifiers
          ? Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  };

  const renderMarketColHeader = (type: string, width: string) => {
    if (type === 'Tipp' || type.includes('1X2') || type === 'Tipp Restzeit') {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">1</div>
          <div className="flex-1 text-center">X</div>
          <div className="flex-1 text-center">2</div>
        </div>
      );
    }
    if (type === 'Über/Unter' || type.includes('Über/Unter')) {
      return (
        <div className={`${width} mx-1 flex gap-1 items-center`}>
          <div className="w-[20px]" />
          <div className="flex-1 text-center">+</div>
          <div className="flex-1 text-center">−</div>
        </div>
      );
    }
    if (type === 'Doppelte Chance') {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">1X</div>
          <div className="flex-1 text-center">12</div>
          <div className="flex-1 text-center">X2</div>
        </div>
      );
    }
    if (type === 'Beide Teams treffen') {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">Ja</div>
          <div className="flex-1 text-center">Nein</div>
        </div>
      );
    }
    return <div className={`${width} mx-1 flex`}><div className="flex-1 text-center">-</div></div>;
  };

  const renderMarketColCells = (ev: EventSnapshot, type: string, width: string, isUp: boolean = false, isDown: boolean = false) => {
    if (type === 'Tipp' || type === 'Tipp Restzeit') {
      const odds3way = find1x2(ev.markets); // fallback mock for unmapped markets inside demo
      return (
        <div className={`${width} flex items-center gap-1.5 flex-shrink-0 mx-1`}>
          {odds3way ? (
            ([
              { label: '1', outcome: odds3way.home, tipp: type === 'Tipp Restzeit' ? 'Heimsieg Restzeit' : 'Heimsieg' },
              { label: 'X', outcome: odds3way.draw, tipp: type === 'Tipp Restzeit' ? 'Unent. Restzeit' : 'Unentschieden' },
              { label: '2', outcome: odds3way.away, tipp: type === 'Tipp Restzeit' ? 'Auswärtssieg Restzeit' : 'Auswärtssieg' },
            ] as const).map(({ label, outcome, tipp }, i) => {
              const sel = isSelected(ev.event_id, outcome.outcome_id);
              const isU = isUp && i === 1; // mock live tick
              const isD = isDown && i === 2; // mock live tick
              const textColor = isU ? 'text-[#00a650]' : isD ? 'text-[#cc0000]' : 'text-gray-800';
              return (
                <button key={label} onClick={() => handleOddsClick(ev, odds3way.market, outcome, tipp)}
                  className={`flex-1 rounded h-9 text-[13px] font-bold transition-all duration-200 border flex items-center justify-center relative overflow-hidden ${sel ? 'bg-gray-900 text-white border-gray-900' : 'bg-tipico-gray-odds hover:bg-gray-200 border-transparent'} ${sel ? '' : textColor}`}>
                  {isU && !sel && <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-[#00a650]"></div>}
                  {isD && !sel && <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-tipico-red"></div>}
                  {formatOdds(outcome.odds)}
                </button>
              );
            })
          ) : <div className="flex-1 text-center text-xs text-gray-300">—</div>}
        </div>
      );
    }
    if (type === 'Über/Unter' || type.includes('Über/Unter')) {
      const ou = findOU(ev.markets);
      return (
        <div className={`${width} flex items-center gap-1.5 flex-shrink-0 mx-1`}>
          {ou ? (
            <>
              <div className="w-[20px] text-center text-[11px] text-gray-400">2,5</div>
              {([
                { label: '+', outcome: ou.over, tipp: 'Über 2,5' },
                { label: '−', outcome: ou.under, tipp: 'Unter 2,5' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, outcome.outcome_id);
                return (
                  <button key={label} onClick={() => handleOddsClick(ev, ou.market, outcome, tipp)}
                    className={`flex-1 rounded h-9 text-[13px] font-bold transition-all duration-200 border bg-tipico-gray-odds hover:bg-gray-200 ${sel ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-800 border-transparent'}`}>
                    {formatOdds(outcome.odds)}
                  </button>
                );
              })}
            </>
          ) : <div className="flex-1 text-center text-xs text-gray-300">—</div>}
        </div>
      );
    }
    // Mock empty boxes for other unmapped options
    return (
      <div className={`${width} flex items-center justify-center flex-shrink-0 mx-1`}>
        <div className="flex-1 text-center text-xs text-gray-300">—</div>
      </div>
    );
  };

  // Betting slip
  const totalOdds = bets.reduce((acc, bet) => acc * parseFloat(bet.odds.replace(',', '.')), 1);
  const stakeNum = parseFloat(stake) || 0;
  const possibleWin = (stakeNum * totalOdds).toFixed(2).replace('.', ',');

  const handlePlaceBet = async () => {
    const selections = bets
      .filter((b) => b.eventId && b.marketId && b.outcomeId)
      .map((b) => ({
        event_id: b.eventId!,
        market_id: b.marketId!,
        outcome_id: b.outcomeId!,
        ...(b.specifiers ? { specifiers: b.specifiers } : {}),
      }));
    if (!selections.length) return;
    setPlacing(true);
    setBetStatus('idle');
    try {
      await placeBet({ ticket_type: 'single', stake: stakeNum, selections });
      setBetStatus('success');
      onClearAll();
    } catch (err: any) {
      setBetError(err.message || 'Fehler beim Platzieren.');
      setBetStatus('error');
    } finally {
      setPlacing(false);
    }
  };

  // ── Left sidebar JSX (Sports / Competitions) ──────────────────────────
  const leftSidebar = (
    <aside className="w-[240px] flex-shrink-0 sticky top-[80px] max-h-[calc(100vh-100px)] flex flex-col gap-6 overflow-y-auto pr-2 scrollbar-hide">
      {/* Search */}
      <div className="relative">
        <input type="text" placeholder="Suchen" className="w-full bg-white border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-tipico-red transition shadow-sm" />
        <svg className="absolute right-4 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
      </div>

      {/* Schnellzugriff */}
      <div>
        <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Schnellzugriff</h3>
        <div className="flex flex-col bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
          {[
            { icon: '🥇', name: 'Top-Events' },
            { icon: '⭐', name: 'Top-Kombis' },
            { icon: '📅', name: 'Spiele von heute' },
            { icon: '🇩🇪', name: 'Deutscher Fußball' },
            { icon: '🌍', name: 'Europäischer Fußball' },
          ].map((item) => (
            <button key={item.name} className="flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-100 transition border-b border-gray-50 last:border-0 group">
              <span className="text-[18px] w-6 text-center">{item.icon}</span>
              <span className="text-[13px] font-bold text-gray-800 flex-1 truncate">{item.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Top Competitions */}
      <div>
        <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-widest mb-3 px-1">Top-Wettbewerbe</h3>
        <div className="flex flex-col bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
          {TOP_COMPETITIONS.map((comp) => (
            <button key={comp.name} className="flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition border-b border-gray-50 last:border-0">
              <span className="text-base w-6 text-center">{comp.flag}</span>
              <span className="text-[13px] font-bold text-gray-800 flex-1 truncate">{comp.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Top-Sportarten */}
      <div className="flex flex-col gap-1">
        <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Top-Sportarten</h3>
        <div className="flex flex-col bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 mb-4">
          {['Soccer', 'Tennis', 'Basketball', 'Ice Hockey', 'Handball'].map((topName) => {
            const s = sports.find(sp => sp.sport_name === topName);
            if (!s) return null;
            const isSportExpanded = expandedSport === s.sport_name;
            const displayName = s.sport_name === 'Soccer' ? 'Fußball' : s.sport_name === 'Ice Hockey' ? 'Eishockey' : s.sport_name;
            const countries = sportsTree[s.sport_name] || {};

            return (
              <div key={s.sport_id} className="flex flex-col border-b border-gray-50 last:border-0">
                <button 
                  onClick={() => {
                    setExpandedSport(isSportExpanded ? null : s.sport_name);
                    if (activeSport !== s.sport_name) {
                      setActiveSport(s.sport_name);
                      setSelectedTournaments(new Set());
                    }
                  }} 
                  className={`flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-100 transition ${activeSport === s.sport_name ? 'bg-gray-50' : ''}`}
                >
                  <span className="text-lg w-6 text-center">{SPORT_EMOJI[s.sport_name] || '🏅'}</span>
                  <span className={`text-[13px] flex-1 truncate ${activeSport === s.sport_name ? 'font-black text-tipico-red' : 'font-bold text-gray-800'}`}>{displayName}</span>
                  <svg className={`w-3 h-3 text-gray-400 transition-transform ${isSportExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
                </button>

                {/* Countries / Categories */}
                {isSportExpanded && (
                  <div className="bg-gray-50/50 flex flex-col">
                    {Object.entries(countries).sort().map(([countryName, tournaments]) => {
                      const isCountryExpanded = expandedCategory === countryName;
                      const flag = COUNTRY_FLAGS[countryName] || '🏳️';
                      const allT = Object.keys(tournaments);
                      const selectedCount = allT.filter(t => selectedTournaments.has(t)).length;
                      const allChecked = selectedCount === allT.length;
                      const partialChecked = selectedCount > 0 && !allChecked;
                      return (
                        <div key={countryName} className="flex flex-col border-t border-gray-100/50">
                          <div className="flex items-center pl-4 pr-4 py-2 hover:bg-gray-100 transition gap-2">
                            {/* Country checkbox */}
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleCountry(tournaments); }}
                              className={`w-4 h-4 flex-shrink-0 rounded-sm border-2 flex items-center justify-center transition-colors ${
                                allChecked ? 'bg-tipico-red border-tipico-red' : partialChecked ? 'bg-tipico-red/20 border-tipico-red' : 'border-gray-300 hover:border-tipico-red'
                              }`}
                            >
                              {allChecked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                              {partialChecked && <div className="w-2 h-0.5 bg-tipico-red" />}
                            </button>
                            <span className="text-[14px] w-5 text-center flex-shrink-0">{flag}</span>
                            <span className="text-[12px] font-bold text-gray-700 flex-1 truncate">{countryName}</span>
                            {selectedCount > 0 && <span className="text-[10px] font-black text-tipico-red">{selectedCount}</span>}
                            <button onClick={() => setExpandedCategory(isCountryExpanded ? null : countryName)} className="p-1">
                              <svg className={`w-2.5 h-2.5 text-gray-400 transition-transform ${isCountryExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                          </div>

                          {/* Tournaments */}
                          {isCountryExpanded && (
                            <div className="flex flex-col bg-white/50">
                              {Object.entries(tournaments).sort().map(([t, count]) => {
                                const isChecked = selectedTournaments.has(t);
                                return (
                                  <button
                                    key={t}
                                    onClick={() => toggleTournament(t)}
                                    className="pl-12 pr-4 py-2 text-left hover:bg-gray-100 transition flex items-center gap-2 group"
                                  >
                                    <div className={`w-3.5 h-3.5 flex-shrink-0 rounded-sm border-2 flex items-center justify-center transition-colors ${
                                      isChecked ? 'bg-tipico-red border-tipico-red' : 'border-gray-300 group-hover:border-tipico-red'
                                    }`}>
                                      {isChecked && <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                    </div>
                                    <span className={`text-[11px] font-medium flex-1 truncate ${
                                      isChecked ? 'text-tipico-red font-bold' : 'text-gray-500 group-hover:text-tipico-red'
                                    }`}>{t}</span>
                                    <span className="text-[10px] font-bold text-gray-400">{count}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Alle Sportarten */}
      <div className="flex flex-col gap-1">
        <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Alle Sportarten</h3>
        <div className="flex flex-col bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
          {sports.sort((a, b) => a.sport_name.localeCompare(b.sport_name)).map((s) => {
            const isSportExpanded = expandedSport === s.sport_name;
            const displayName = s.sport_name === 'Soccer' ? 'Fußball' : s.sport_name === 'Ice Hockey' ? 'Eishockey' : s.sport_name;
            const countries = sportsTree[s.sport_name] || {};

            return (
              <div key={s.sport_id} className="flex flex-col border-b border-gray-50 last:border-0">
                <button
                  onClick={() => { setExpandedSport(isSportExpanded ? null : s.sport_name); setActiveSport(s.sport_name); }}
                  className={`flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-100 transition ${activeSport === s.sport_name ? 'bg-gray-50' : ''}`}
                >
                  <span className="text-lg w-6 text-center">{SPORT_EMOJI[s.sport_name] || '🏅'}</span>
                  <span className={`text-[13px] flex-1 truncate ${activeSport === s.sport_name ? 'font-black text-tipico-red' : 'font-bold text-gray-800'}`}>{displayName}</span>
                  <svg className={`w-3 h-3 text-gray-400 transition-transform ${isSportExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
                </button>

                {isSportExpanded && (
                  <div className="bg-gray-50/50 flex flex-col">
                    {Object.entries(countries).sort().map(([countryName, tournaments]) => {
                      const isCountryExpanded = expandedCategory === countryName;
                      const flag = COUNTRY_FLAGS[countryName] || '🏳️';
                      const allT = Object.keys(tournaments);
                      const selectedCount = allT.filter(t => selectedTournaments.has(t)).length;
                      const allChecked = selectedCount === allT.length;
                      const partialChecked = selectedCount > 0 && !allChecked;
                      return (
                        <div key={countryName} className="flex flex-col border-t border-gray-100/50">
                          <div className="flex items-center pl-4 pr-4 py-2 hover:bg-gray-100 transition gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleCountry(tournaments); }}
                              className={`w-4 h-4 flex-shrink-0 rounded-sm border-2 flex items-center justify-center transition-colors ${
                                allChecked ? 'bg-tipico-red border-tipico-red' : partialChecked ? 'bg-tipico-red/20 border-tipico-red' : 'border-gray-300 hover:border-tipico-red'
                              }`}
                            >
                              {allChecked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                              {partialChecked && <div className="w-2 h-0.5 bg-tipico-red" />}
                            </button>
                            <span className="text-[14px] w-5 text-center flex-shrink-0">{flag}</span>
                            <span className="text-[12px] font-bold text-gray-700 flex-1 truncate">{countryName}</span>
                            {selectedCount > 0 && <span className="text-[10px] font-black text-tipico-red">{selectedCount}</span>}
                            <button onClick={() => setExpandedCategory(isCountryExpanded ? null : countryName)} className="p-1">
                              <svg className={`w-2.5 h-2.5 text-gray-400 transition-transform ${isCountryExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                          </div>
                          {isCountryExpanded && (
                            <div className="flex flex-col bg-white/50">
                              {Object.entries(tournaments).sort().map(([t, count]) => {
                                const isChecked = selectedTournaments.has(t);
                                return (
                                  <button
                                    key={t}
                                    onClick={() => toggleTournament(t)}
                                    className="pl-12 pr-4 py-2 text-left hover:bg-gray-100 transition flex items-center gap-2 group"
                                  >
                                    <div className={`w-3.5 h-3.5 flex-shrink-0 rounded-sm border-2 flex items-center justify-center transition-colors ${
                                      isChecked ? 'bg-tipico-red border-tipico-red' : 'border-gray-300 group-hover:border-tipico-red'
                                    }`}>
                                      {isChecked && <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                    </div>
                                    <span className={`text-[11px] font-medium flex-1 truncate ${
                                      isChecked ? 'text-tipico-red font-bold' : 'text-gray-500 group-hover:text-tipico-red'
                                    }`}>{t}</span>
                                    <span className="text-[10px] font-bold text-gray-400">{count}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );

  // ── Live Page Content JSX ──────────────────────────────────────────────
  const livePageContent = (
    <div className="flex flex-col gap-6">
      {/* ── LIVE SPORT TABS ───────────────────────────────────────────── */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {allLiveSportTabs.map((s) => (
          <button
            key={s.name}
            onClick={() => setActiveSport(s.name)}
            className={`relative w-[110px] h-[86px] flex-shrink-0 bg-white rounded-lg shadow-sm border-b-[4px] transition group ${
              activeSport === s.name
                ? 'border-tipico-red'
                : 'border-transparent hover:border-gray-200'
            }`}
          >
            <span className="absolute top-2 right-3 text-[10px] font-black text-gray-400">
              {s.count}
            </span>
            <div className="flex flex-col items-center justify-center h-full pt-1">
              <span className="text-3xl leading-none">{s.emoji}</span>
              <span className={`text-[12px] mt-2 font-bold tracking-tight ${activeSport === s.name ? 'text-gray-900' : 'text-gray-500'}`}>
                {s.name === 'Soccer' ? 'Fußball' : s.name}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* ── SORT & FILTER BAR ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Sortieren nach:</span>
          <div className="flex bg-white rounded-full border border-gray-200 p-[3px] shadow-sm">
            {(['zeit', 'wettbewerb'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-5 py-1.5 rounded-full text-[12px] font-black transition ${
                  sortBy === s
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {s === 'zeit' ? 'Zeit' : 'Wettbewerb'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── LIVE MATCH LIST ────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-2 flex justify-end bg-gray-50 border-b border-gray-100">
           <div className="w-[192px] mx-1 text-[11px] font-black text-gray-400 text-center uppercase tracking-widest">1X2</div>
           <div className="w-[192px] mx-1 text-[11px] font-black text-gray-400 text-center uppercase tracking-widest">Restzeit</div>
           <div className="w-[52px]" />
        </div>

        {Object.entries(
          filteredLive.reduce((acc, ev) => {
            const t = ev.event.tournament_name;
            if (!acc[t]) acc[t] = [];
            acc[t].push(ev);
            return acc;
          }, {} as Record<string, EventSnapshot[]>)
        ).map(([tournament, events]) => (
          <React.Fragment key={tournament}>
            <div className="px-5 py-3 bg-gray-50/50 border-t border-b border-gray-100 flex items-center gap-2">
              <span className="text-[13px] font-black text-gray-900">{tournament}</span>
            </div>
            {events.map((ev) => {
              const clockStr = formatClock(ev) || "HZ";
              const homeScore = ev.status?.home_score ?? 0;
              const awayScore = ev.status?.away_score ?? 0;
              const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;

              return (
                <div key={ev.event_id} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition border-b border-gray-50 last:border-0 bg-white">
                  <div className="w-[60px] flex-shrink-0 text-left border-r border-gray-100 pr-2">
                    <div className="text-[13px] font-black text-gray-900">{clockStr}</div>
                    <div className="text-[9px] font-black text-tipico-red tracking-widest mt-0.5 animate-pulse">LIVE</div>
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer" onClick={() => setSelectedEvent(ev)}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[13px] font-black text-gray-900 truncate">{ev.event.home.name}</span>
                      <span className="text-[14px] font-black text-blue-600">{homeScore}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-black text-gray-900 truncate">{ev.event.away.name}</span>
                      <span className="text-[14px] font-black text-blue-600">{awayScore}</span>
                    </div>
                  </div>

                  {renderMarketColCells(ev, 'Tipp', 'w-[192px]')}
                  {renderMarketColCells(ev, 'Tipp Restzeit', 'w-[192px]')}

                  <button onClick={() => setSelectedEvent(ev)} className="w-[52px] flex items-center justify-end text-[12px] font-black text-gray-400 hover:text-tipico-red transition">
                    +{total}
                    <svg className="w-4 h-4 ml-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
  const desktopHeader = (
    <header className="bg-tipico-red sticky top-0 z-50 shadow-md">
      <div className="w-full max-w-[1440px] mx-auto px-6 flex items-center h-[64px] gap-10">
        <span className="text-white text-3xl font-black tracking-tighter">TIPICO</span>
        <nav className="flex gap-1 h-full">
          {[
            { id: 'home', label: 'Sportwetten' },
            { id: 'live', label: 'Livewetten' },
            { id: 'games', label: 'Games' },
            { id: 'aktionen', label: 'Aktionen' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => item.id !== 'aktionen' && onNavigate(item.id)}
              className={`text-[13px] font-bold h-full px-4 transition uppercase tracking-wider flex items-center ${
                activePage === item.id
                  ? 'text-white border-b-4 border-white'
                  : 'text-white/80 border-b-4 border-transparent hover:text-white hover:bg-tipico-red-dark'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-4 relative">
          {isLogged ? (
            <>
              <div className="flex items-center gap-6">
                {/* Einzahlung Block */}
                <button className="flex flex-col items-center justify-center bg-black/20 hover:bg-black/30 rounded px-5 py-1.5 transition cursor-pointer">
                  <span className="text-[10px] text-white/90 leading-tight tracking-wide">Einzahlung</span>
                  <span className="text-[14px] font-bold text-white leading-tight mt-0.5">
                    {wallet ? `${parseFloat(wallet.balance_cash).toFixed(2)} €` : '0,00 €'}
                  </span>
                </button>
                
                {/* Header Icons */}
                <div className="flex items-center gap-6">
                  {/* Meine Wetten */}
                  <button onClick={() => onNavigate?.('bets')} className="flex flex-col items-center justify-center gap-[2px] text-white hover:text-white/80 transition cursor-pointer">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <span className="text-[10px] font-medium tracking-wide">Meine Wetten</span>
                  </button>

                  {/* Neuigkeiten */}
                  <button className="flex flex-col items-center justify-center gap-[2px] text-white hover:text-white/80 transition cursor-pointer relative">
                    <div className="relative">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span className="absolute -top-1.5 -right-2 bg-[#17a2b8] text-white text-[9px] font-bold px-[5px] py-[2px] shadow-sm rounded-full leading-none">1</span>
                    </div>
                    <span className="text-[10px] font-medium tracking-wide">Neuigkeiten</span>
                  </button>

                  {/* Konto Button Toggle */}
                  <button 
                    onClick={() => { setIsProfileOpen(false); onNavigate?.('account'); }}
                    className="flex flex-col items-center justify-center gap-[2px] text-white hover:text-white/80 transition cursor-pointer"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-[10px] font-medium tracking-wide">Konto</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <button onClick={onLoginClick} className="text-white font-bold text-sm hover:opacity-80 transition">Anmelden</button>
              <button onClick={onRegisterClick} className="bg-white text-tipico-red px-6 py-2 rounded-full text-sm font-bold shadow-md hover:bg-gray-100 transition">Registrieren</button>
            </>
          )}
        </div>
      </div>
    </header>
  );

  // ── Right sidebar JSX (Wettschein + Features) ────────────────────────────
  const rightSidebar = (
    <aside className="w-[320px] flex-shrink-0 sticky top-[80px] max-h-[calc(100vh-100px)] flex flex-col gap-4 transition-all duration-300">
      <div className={`bg-white rounded-lg shadow-lg overflow-hidden flex flex-col border border-gray-100 transition-[max-height] duration-500 ease-in-out ${bets.length > 0 || rightSidebarTab === 'offene' ? 'max-h-[calc(100vh-120px)]' : 'max-h-[300px]'}`}>
        <div className="flex bg-white">
          <div 
            onClick={() => setRightSidebarTab('wettschein')}
            className={`flex-1 py-3 text-center cursor-pointer transition-all ${rightSidebarTab === 'wettschein' ? 'border-b-[3px] border-tipico-red' : 'border-b-[3px] border-transparent hover:bg-gray-50'}`}
          >
            <div className="text-[22px] font-bold text-gray-900 leading-none mb-1">{bets.length}</div>
            <div className={`text-[11px] ${rightSidebarTab === 'wettschein' ? 'text-gray-900 font-bold' : 'text-gray-500'}`}>Wettschein</div>
          </div>
          <div 
            onClick={() => setRightSidebarTab('offene')}
            className={`flex-1 py-3 text-center cursor-pointer transition-all ${rightSidebarTab === 'offene' ? 'border-b-[3px] border-tipico-red' : 'border-b-[3px] border-transparent hover:bg-gray-50'}`}
          >
            <div className="text-[22px] font-bold text-gray-900 leading-none mb-1">{tickets.filter(t => t.status === 'open').length}</div>
            <div className={`text-[11px] ${rightSidebarTab === 'offene' ? 'text-gray-900 font-bold' : 'text-gray-500'}`}>Offene Wetten</div>
          </div>
        </div>
          <div className="flex-1 overflow-y-auto min-h-0 bg-gray-50">
  {rightSidebarTab === 'wettschein' && (
    bets.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-10 px-6 text-center bg-white">
        <svg className="w-10 h-10 text-gray-300 mb-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 2v20l2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2V2H4zm14 16H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V8h12v2z" />
        </svg>
        <p className="text-[13px] text-gray-400 font-medium">Dein Wettschein ist noch leer.</p>
        <p className="text-[13px] text-gray-400 font-medium mt-1">Wähle jetzt deine Wetten aus.</p>
      </div>
    ) : (
      <>
        <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-gray-100">
          <div className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 cursor-pointer hover:border-gray-400">
            <span className="text-xs font-semibold text-gray-800">System</span>
            <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </div>
          <button onClick={onClearAll} className="text-gray-400 hover:text-gray-700 transition">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M4 7h16" />
            </svg>
          </button>
        </div>
        {bets.map((bet) => (
          <div key={bet.id} className="bg-white border-b border-gray-100 flex items-stretch last:border-0 hover:bg-gray-50 flex-row">
            <div className="w-[30px] flex items-start justify-center pt-3 border-r border-gray-50">
              <div className="w-4 h-4 border border-gray-300 text-gray-300 text-[9px] font-bold flex items-center justify-center rounded-sm">B</div>
            </div>
            <div className="flex-1 px-3 py-3 font-sans">
              <div className="flex justify-between items-start mb-0.5">
                <div className="text-[12px] text-gray-900 pr-2">
                  <span className="text-gray-500 font-normal">Tipp: </span><span className="font-bold">{bet.tippLabel}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-800">{bet.odds}</span>
                  <button onClick={() => onRemoveBet(bet.id)} className="text-gray-300 hover:text-gray-600 transition -mt-0.5">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="text-[11px] text-gray-500 truncate leading-snug">{bet.matchName}</div>
            </div>
          </div>
        ))}
      </>
    )
  )}
  {rightSidebarTab === 'offene' && (
    tickets.filter(t => t.status === 'open').length === 0 ? (
      <div className="flex flex-col items-center justify-center py-10 px-6 text-center bg-white">
        <p className="text-[13px] text-gray-400 font-medium">Du hast keine offenen Wetten.</p>
      </div>
    ) : (
      <div className="flex flex-col gap-2 p-2">
        {tickets.filter(t => t.status === 'open').map(t => (
          <div key={t.id} className="p-3 border border-gray-200 rounded shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold text-gray-700">Kombi</span>
              <span className="text-xs font-bold text-gray-900">{t.total_stake} €</span>
            </div>
            <div className="flex justify-between items-center text-[10px] text-gray-500 mb-2 border-b border-gray-100 pb-2">
              <span>Quoten: {t.accepted_total_odds}</span>
              <span className="text-green-600 font-bold">Mögl. Gewinn: {t.potential_payout} €</span>
            </div>
            <button className="w-full mt-1 bg-green-500 text-white text-[11px] font-bold py-1.5 rounded hover:bg-green-600 transition">
              Cashout anfragen
            </button>
          </div>
        ))}
      </div>
    )
  )}
</div>
{rightSidebarTab === 'wettschein' && bets.length > 0 && (
            <div className="bg-white border-t border-gray-200 mt-2">
              <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100">
                <span className="text-[12px] font-bold text-gray-700">Gesamtquote</span>
                <span className="text-sm font-black text-gray-900">{totalOdds.toFixed(2).replace('.', ',')}</span>
              </div>
              <div className="px-3 py-3">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 relative">
                    <input 
                      type="number" 
                      value={stake} 
                      onChange={(e) => setStake(e.target.value)} 
                      className="w-full text-right text-sm font-bold text-gray-800 border focus:border-tipico-red rounded py-2 pr-7 pl-2 outline-none transition transition-shadow focus:shadow-inner" 
                      min="0" 
                      placeholder="0,00"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500">€</span>
                  </div>
                  <div className="w-[120px] text-right">
                    <p className="text-[9px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Mögl. Gewinn</p>
                    <p className="text-base font-black text-green-600 leading-none">{possibleWin} €</p>
                  </div>
                </div>
                
                {betStatus === 'success' && <p className="text-green-600 text-xs text-center font-bold mb-3 bg-green-50 py-1.5 rounded">Wette erfolgreich platziert! ✓</p>}
                {betStatus === 'error' && <p className="text-red-600 text-xs text-center font-bold mb-3 bg-red-50 py-1.5 rounded">{betError}</p>}
                
                {isLogged ? (
                  <button onClick={handlePlaceBet} disabled={placing || stakeNum <= 0} className="w-full bg-[#00a650] text-white text-sm font-bold py-3 rounded uppercase tracking-wider hover:bg-[#009045] transition disabled:opacity-60 disabled:cursor-not-allowed">
                    {placing ? 'Wird platziert…' : 'Wette platzieren'}
                  </button>
                ) : (
                  <button onClick={onLoginClick} className="w-full bg-tipico-red text-white text-sm font-bold py-3 rounded uppercase tracking-wider hover:bg-red-700 transition">Anmelden</button>
                )}
              </div>
            </div>
          )}
        </div>

    </aside>
  );

  // ── Match detail overlay ─────────────────────────────────────────────────


  // ── Live page ─────────────────────────────────────────────────────────────

  if (activePage === 'live') {
    const liveSportEvents = liveEvents.filter((ev) => ev.event.sport_name === activeSport);
    const sorted = sortBy === 'wettbewerb'
      ? [...liveSportEvents].sort((a, b) => a.event.tournament_name.localeCompare(b.event.tournament_name))
      : [...liveSportEvents].sort((a, b) => {
          const aMin = parseInt(a.status?.clock?.split(':')[0] || '0');
          const bMin = parseInt(b.status?.clock?.split(':')[0] || '0');
          return bMin - aMin;
        });
    const next6h = preEvents.filter((ev) => ev.event.sport_name === activeSport && isWithinHours(ev, 6));

    // Group by tournament when wettbewerb sort
    const tournamentGroups: Record<string, EventSnapshot[]> = {};
    if (sortBy === 'wettbewerb') {
      for (const ev of sorted) {
        const t = ev.event.tournament_name;
        if (!tournamentGroups[t]) tournamentGroups[t] = [];
        tournamentGroups[t].push(ev);
      }
    }

    const columnHeaders = (
      <>
        <div className="px-5 py-2.5 flex items-center bg-[#f7f8f9] border-b border-gray-200">
          <div className="flex-1" />
          <div className="w-[200px] bg-white border border-gray-200 rounded px-3 py-1.5 flex items-center justify-between text-[12px] font-bold text-gray-700 mx-1">
            <span>Tipp</span>
            <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          <div className="w-[120px] bg-white border border-gray-200 rounded px-3 py-1.5 flex items-center justify-between text-[12px] font-bold text-gray-700 mx-1">
            <span>Über/Unter</span>
            <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          <div className="w-[60px]" />
        </div>
        <div className="px-5 py-1.5 flex items-center bg-[#f7f8f9] border-b border-gray-200 text-[11px] text-gray-500 font-medium">
          <div className="w-[68px]" />
          <div className="flex-1" />
          <div className="w-[200px] flex gap-1 mx-1">
            {['1', 'X', '2'].map((l) => <div key={l} className="flex-1 text-center">{l}</div>)}
          </div>
          <div className="w-[120px] flex gap-1 mx-1">
            <div className="w-[24px]" />
            <div className="flex-1 text-center">+</div>
            <div className="flex-1 text-center">−</div>
          </div>
          <div className="w-[60px]" />
        </div>
      </>
    );

    const renderLiveOddsRow = (ev: EventSnapshot) => {
      const odds3way = find1x2(ev.markets);
      const ou = findOU(ev.markets);
      const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
      const redHome = (ev.status?.statistics?.red_cards?.home || 0) > 0;
      const redAway = (ev.status?.statistics?.red_cards?.away || 0) > 0;

      return (
        <div key={ev.event_id} className="px-5 py-3 flex items-stretch gap-3 hover:bg-gray-50 transition border-b border-gray-200 bg-white">
          {/* Clock */}
          <div className="w-[56px] flex-shrink-0 cursor-pointer pt-0.5" onClick={() => setSelectedEvent(ev)}>
            <div className="text-[13px] font-black text-gray-900">{formatClock(ev)}</div>
            <div className="mt-0.5 text-[9px] font-bold tracking-widest text-red-600">LIVE</div>
          </div>
          
          {/* Teams + Score */}
          <div className="flex-1 min-w-0 flex items-stretch cursor-pointer border-r border-gray-100 pr-4" onClick={() => setSelectedEvent(ev)}>
            <div className="w-6 flex flex-col justify-between py-0.5">
              <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm flex items-center justify-center">
                <span className="text-[8px] font-black">{ev.event.home.name.charAt(0)}</span>
              </div>
              <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm flex items-center justify-center">
                <span className="text-[8px] font-black">{ev.event.away.name.charAt(0)}</span>
              </div>
            </div>
            <div className="flex-1 flex flex-col justify-between py-0.5 ml-2">
              <div className="flex items-center">
                <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
                {redHome && <span className="w-1.5 h-2.5 bg-[#cc0000] rounded-sm ml-1.5 shadow-sm" />}
              </div>
              <div className="flex items-center">
                <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
                {redAway && <span className="w-1.5 h-2.5 bg-[#cc0000] rounded-sm ml-1.5 shadow-sm" />}
              </div>
            </div>
            <div className="flex flex-col justify-between py-0.5 ml-2 text-right">
              <div className="text-[13px] font-black text-[#00aaff]">{ev.status?.home_score ?? 0}</div>
              <div className="text-[13px] font-black text-[#00aaff]">{ev.status?.away_score ?? 0}</div>
            </div>
          </div>

          {/* Tipp 1X2 */}
          <div className="w-[192px] flex items-center gap-1.5 flex-shrink-0 mx-1">
            {odds3way ? (
              ([
                { label: '1', outcome: odds3way.home, tipp: 'Heimsieg' },
                { label: 'X', outcome: odds3way.draw, tipp: 'Unentschieden' },
                { label: '2', outcome: odds3way.away, tipp: 'Auswärtssieg' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, outcome.outcome_id);
                return (
                  <button
                    key={label}
                    onClick={() => handleOddsClick(ev, odds3way.market, outcome, tipp)}
                    className={`flex-1 rounded h-10 text-[13px] font-bold transition-all duration-200 border ${
                      sel ? 'bg-gray-900 text-white border-gray-900' : 'bg-tipico-gray-odds hover:bg-gray-200 text-gray-800 border-transparent'
                    }`}
                  >
                    {formatOdds(outcome.odds)}
                  </button>
                );
              })
            ) : (
              <div className="flex-1 text-center text-xs text-gray-300">—</div>
            )}
          </div>

          {/* Über/Unter */}
          <div className="w-[124px] flex items-center gap-1.5 flex-shrink-0 mx-1">
            {ou ? (
              <>
                <div className="w-[20px] text-center text-[10px] font-bold text-gray-400">0,5</div>
                {([
                  { label: '+', outcome: ou.over, tipp: 'Über 0,5' },
                  { label: '−', outcome: ou.under, tipp: 'Unter 0,5' },
                ] as const).map(({ label, outcome, tipp }) => {
                  const sel = isSelected(ev.event_id, outcome.outcome_id);
                  return (
                    <button
                      key={label}
                      onClick={() => handleOddsClick(ev, ou.market, outcome, tipp)}
                      className={`flex-1 rounded h-10 text-[13px] font-bold transition-all duration-200 border ${
                        sel ? 'bg-gray-900 text-white border-gray-900' : 'bg-tipico-gray-odds hover:bg-gray-200 text-gray-800 border-transparent'
                      }`}
                    >
                      {formatOdds(outcome.odds)}
                    </button>
                  );
                })}
              </>
            ) : (
              <div className="flex-1 text-center text-xs text-gray-300">—</div>
            )}
          </div>

          {/* Market count */}
          <button onClick={() => setSelectedEvent(ev)} className="w-[52px] flex items-center justify-end text-[12px] font-bold text-gray-500 hover:text-gray-800 transition">
            <svg className="w-3.5 h-3.5 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
            {total} ›
          </button>
        </div>
      );
    };

    return (
      <div className="min-h-screen bg-[#f0f0f0]">
        {desktopHeader}
        <div className="w-[98%] mx-auto px-6 py-6 flex gap-6 items-start">

          {/* ── MAIN CONTENT ──────────────────────────────────────────── */}
          <main className="flex-1 min-w-0 flex flex-col gap-6 transition-all">

            {/* Sport tabs */}
            {allLiveSportTabs.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {allLiveSportTabs.map((s) => (
                  <button
                    key={s.name}
                    onClick={() => setActiveSport(s.name)}
                    className={`relative w-[100px] h-[76px] flex-shrink-0 bg-white rounded shadow-sm border-b-[3px] transition group ${
                      activeSport === s.name
                        ? 'border-[#cc0000]'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <span className="absolute top-1.5 right-2 text-[10px] font-bold text-gray-400 group-hover:text-gray-600 transition">
                      {s.count}
                    </span>
                    <div className="flex flex-col items-center justify-center h-full pt-1">
                      <span className="text-2xl leading-none">{s.emoji}</span>
                      <span className={`text-[12px] mt-1.5 font-medium ${activeSport === s.name ? 'text-gray-900' : 'text-gray-600'}`}>
                        {s.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Sort bar */}
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-medium text-gray-500">Sortiere nach:</span>
              <div className="flex bg-white rounded-full border border-gray-200 p-[3px] shadow-sm">
                {(['zeit', 'wettbewerb'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`px-5 py-1.5 rounded-full text-[12px] font-bold transition ${
                      sortBy === s
                        ? 'bg-white border border-gray-200 shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {s === 'zeit' ? 'Zeit' : 'Wettbewerb'}
                  </button>
                ))}
              </div>
            </div>

            {/* Live matches */}
            {loading ? (
              <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#cc0000] border-t-transparent" /></div>
            ) : liveSportEvents.length === 0 ? (
              <div className="bg-white rounded shadow-sm p-10 text-center text-gray-400 text-[13px] font-medium">
                Keine Live-Ereignisse für {activeSport}.
              </div>
            ) : (
              <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-200">
                {/* Section header */}
                <div className="px-5 py-3 bg-white flex items-center gap-3 border-b border-gray-200">
                  <span className="text-xl leading-none">{SPORT_EMOJI[activeSport] || '🏅'}</span>
                  <span className="text-[15px] font-bold text-gray-900">{activeSport}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-[12px] font-medium text-gray-500">{liveSportEvents.length} Spiele</span>
                  </div>
                </div>
                {columnHeaders}
                {sortBy === 'wettbewerb'
                  ? Object.entries(tournamentGroups).map(([tournament, events]) => (
                      <React.Fragment key={tournament}>
                        <div className="px-5 py-2 bg-[#f0f2f5] border-b border-gray-200 flex items-center gap-2">
                          <span className="text-[12px] font-bold text-gray-700">{tournament}</span>
                        </div>
                        {events.map(renderLiveOddsRow)}
                      </React.Fragment>
                    ))
                  : sorted.map(renderLiveOddsRow)
                }
              </div>
            )}

            {/* Nächste 6 Stunden */}
            {next6h.length > 0 && (
              <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-200">
                <div className="px-5 py-3 bg-white flex items-center gap-3 border-b border-gray-200">
                  <span className="text-[15px] font-bold text-gray-900">Nächste 6 Stunden</span>
                  <div className="ml-auto">
                    <span className="text-[12px] font-medium text-gray-500">{next6h.length} Spiele</span>
                  </div>
                </div>
                {columnHeaders}
                {next6h.map((ev) => {
                  const odds3way = find1x2(ev.markets);
                  const ou = findOU(ev.markets);
                  const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
                  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
                  const timeStr = starts ? formatTime(new Date(starts).toISOString()) : '';
                  return (
                    <div key={ev.event_id} className="px-5 py-3 flex items-stretch gap-3 hover:bg-gray-50 transition border-b border-gray-200 bg-white">
                      {/* Clock */}
                      <div className="w-[56px] flex-shrink-0 cursor-pointer pt-0.5" onClick={() => setSelectedEvent(ev)}>
                        <div className="text-[13px] font-medium text-gray-600">{timeStr}</div>
                      </div>

                      {/* Teams */}
                      <div className="flex-1 min-w-0 flex items-stretch cursor-pointer border-r border-gray-100 pr-4" onClick={() => setSelectedEvent(ev)}>
                        <div className="flex-1 flex flex-col justify-between py-0.5 ml-2">
                          <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
                          <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
                        </div>
                      </div>

                      {/* Tipp 1X2 */}
                      <div className="w-[192px] flex items-center gap-1.5 flex-shrink-0 mx-1">
                        {odds3way ? (
                          ([
                            { label: '1', outcome: odds3way.home, tipp: 'Heimsieg' },
                            { label: 'X', outcome: odds3way.draw, tipp: 'Unentschieden' },
                            { label: '2', outcome: odds3way.away, tipp: 'Auswärtssieg' },
                          ] as const).map(({ label, outcome, tipp }) => {
                            const sel = isSelected(ev.event_id, outcome.outcome_id);
                            return (
                              <button key={label} onClick={() => handleOddsClick(ev, odds3way.market, outcome, tipp)}
                                className={`flex-1 rounded h-10 text-[13px] font-bold shadow-sm transition border ${
                                  sel ? 'bg-gray-800 text-white border-gray-800' : 'bg-[#f7f8f9] hover:bg-gray-200 text-gray-800 border-gray-200'
                                }`}>
                                {formatOdds(outcome.odds)}
                              </button>
                            );
                          })
                        ) : <div className="flex-1 text-center text-xs text-gray-300">—</div>}
                      </div>

                      {/* Über/Unter */}
                      <div className="w-[124px] flex items-center gap-1.5 flex-shrink-0 mx-1">
                        {ou ? (
                          <>
                            <div className="w-[20px] text-center text-[10px] font-bold text-gray-400">0,5</div>
                            {([
                              { label: '+', outcome: ou.over, tipp: 'Über 0,5' },
                              { label: '−', outcome: ou.under, tipp: 'Unter 0,5' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, outcome.outcome_id);
                              return (
                                <button key={label} onClick={() => handleOddsClick(ev, ou.market, outcome, tipp)}
                                  className={`flex-1 rounded h-10 text-[13px] font-bold shadow-sm transition border ${
                                    sel ? 'bg-gray-800 text-white border-gray-800' : 'bg-[#f7f8f9] hover:bg-gray-200 text-gray-800 border-gray-200'
                                  }`}>
                                  {formatOdds(outcome.odds)}
                                </button>
                              );
                            })}
                          </>
                        ) : <div className="flex-1 text-center text-xs text-gray-300">—</div>}
                      </div>

                      {/* Market count */}
                      <button onClick={() => setSelectedEvent(ev)} className="w-[52px] flex items-center justify-end text-[12px] font-bold text-gray-500 hover:text-gray-800 transition">
                        <svg className="w-3.5 h-3.5 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
                        {total} ›
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </main>

          {selectedEvent && (
            <div className="w-[360px] flex-shrink-0 flex flex-col bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200 relative sticky top-[80px] h-[calc(100vh-100px)]">
              <button 
                onClick={() => setSelectedEvent(null)}
                className="absolute top-4 right-4 z-[60] bg-white rounded-full p-2 shadow-md hover:bg-gray-100 border border-gray-100 transition"
              >
                <svg className="w-4 h-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex-1 overflow-y-auto min-h-0">
                <MatchDetailPage 
                  event={selectedEvent}
                  onBack={() => setSelectedEvent(null)}
                  bets={bets}
                  onAddBet={onAddBet}
                  onRemoveBet={onRemoveBet}
                  onLoginClick={onLoginClick}
                />
              </div>
            </div>
          )}

          {rightSidebar}
        </div>
      </div>
    );
  }

  if (activePage === 'account') {
    return (
      <div className="min-h-screen bg-tipico-gray-light flex flex-col">
        {desktopHeader}
        <div className="flex-1 flex flex-col py-6">
          <DesktopAccountPage onLoginClick={onLoginClick} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tipico-gray-light">
      {desktopHeader}

      <div className="w-full max-w-[1440px] mx-auto px-6 py-6 flex gap-6 items-start">
        {leftSidebar}

        <main className="flex-1 min-w-0 flex flex-col gap-6 transition-all">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-4 border-tipico-red border-t-transparent" /></div>
          ) : (
            <>
              {activePage === 'chats' || activePage === 'bets' ? (
                <DesktopMyBetsPage onLoginClick={onLoginClick} />
              ) : activePage === 'live' ? livePageContent : (
                <>
              {/* ── TOP-KOMBIS MOCKUP ──────────────────────────────────── */}
              <div>
                <div className="flex items-end justify-between mb-3 px-1">
                  <div>
                    <h2 className="text-[18px] font-bold text-gray-900">Top-Kombis</h2>
                    <p className="text-[12px] text-gray-500">von anderen Tipico Kunden</p>
                  </div>
                  <button className="text-[12px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center gap-1">
                    Alle anzeigen
                    <span className="text-lg leading-none">›</span>
                  </button>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                  {[
                    { hits: '2633', type: '3er', odds: '11,64', picks: [
                      { tipp: 'FSV Mainz 05', match: 'FC St. Pauli - FSV Mainz 05' },
                      { tipp: 'Borussia Dortmund', match: 'Borussia Mönchengladbach - Borussia Dortm.' },
                      { tipp: 'SC Freiburg', match: 'SC Freiburg - VfL Wolfsburg' },
                    ]},
                    { hits: '725', type: '2er', odds: '4,75', picks: [
                      { tipp: 'Borussia Dortmund', match: 'Borussia Mönchengladbach - Borussia Dortm.' },
                      { tipp: 'SC Freiburg', match: 'SC Freiburg - VfL Wolfsburg' },
                    ]},
                    { hits: '718', type: '3er', odds: '13,78', picks: [
                      { tipp: 'FC St. Pauli', match: 'FC St. Pauli - FSV Mainz 05' },
                      { tipp: 'Borussia Dortmund', match: 'Borussia Mönchengladbach - Borussia Dortm.' },
                      { tipp: 'SC Freiburg', match: 'SC Freiburg - VfL Wolfsburg' },
                    ]},
                    { hits: '512', type: '4er', odds: '22,40', picks: [
                      { tipp: 'Bayern München', match: 'Bayern München - Bayer Leverkusen' },
                      { tipp: 'Eintracht Frankfurt', match: 'Eintracht Frankfurt - RB Leipzig' },
                      { tipp: 'VfL Wolfsburg', match: 'SC Freiburg - VfL Wolfsburg' },
                      { tipp: 'VfB Stuttgart', match: 'VfB Stuttgart - Werder Bremen' },
                    ]},
                  ].map((kombi, i) => (
                    <div key={i} className="min-w-[290px] bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col">
                      {/* Stats header */}
                      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                        <div className="text-center">
                          <div className="text-[#00a650] text-[14px] font-black leading-tight">{kombi.hits}</div>
                          <div className="text-[10px] text-gray-400 leading-tight">Wetten platziert</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-900 text-[14px] font-black leading-tight">{kombi.type}</div>
                          <div className="text-[10px] text-gray-400 leading-tight">Kombi</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-900 text-[14px] font-black leading-tight">{kombi.odds}</div>
                          <div className="text-[10px] text-gray-400 leading-tight">Gesamtquote</div>
                        </div>
                      </div>
                      {/* Picks */}
                      <div className="flex-1 flex flex-col px-4 py-2 gap-2">
                        {kombi.picks.map((p, j) => (
                          <div key={j} className="flex flex-col">
                            <div className="text-[12px] leading-snug">
                              <span className="font-normal text-gray-500">Tipp: </span>
                              <span className="font-bold text-gray-900">{p.tipp}</span>
                            </div>
                            <div className="text-[11px] text-gray-400 truncate leading-snug">{p.match}</div>
                          </div>
                        ))}
                      </div>
                      {/* Link */}
                      <div className="border-t border-gray-100 px-4 py-2.5">
                        <button className="text-[#0088cc] text-[12px] font-bold w-full text-center hover:underline">
                          Wettschein ansehen #{i + 1}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── FIRST MATCH LIST MOCK (Like Screenshot Top) ───────────── */}
              {filteredPre.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-[18px] font-bold text-gray-900 mb-3 px-1">Top-Events</h2>
                  <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-200" style={{position: 'relative', zIndex: 10}}>
                    
                    {/* Sport Tabs plain text style */}
                    <div className="flex bg-white px-2 pt-1 border-b border-gray-200">
                      {['Fußball', 'Basketball', 'Eishockey'].map((s) => {
                        const sEng = s === 'Fußball' ? 'Soccer' : s === 'Eishockey' ? 'Ice Hockey' : s;
                        return (
                          <button
                            key={s}
                            onClick={() => setActiveSport(sEng)}
                            className={`px-5 py-3 text-[14px] font-bold transition border-b-[3px] -mb-[1px] ${
                              activeSport === sEng
                                ? 'border-[#cc0000] text-gray-900'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>

                    {/* Table Dropdowns Top */}
                    <div className="px-5 py-2 flex justify-end bg-[#f7f8f9] border-b border-gray-200">
                      <div className="relative">
                        <div onClick={() => setOpenDropdown(o => o === 't1' ? null : 't1')} className="w-[192px] mx-1 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                           <span className="text-[12px] font-bold text-gray-700">{topMarket1}</span>
                           <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        {openDropdown === 't1' && (
                          <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                            <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                            {MARKET_OPTIONS.map(opt => (
                              <button key={opt} onClick={() => { setTopMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 flex items-center gap-2">
                                <div className="w-4">{topMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                                <span className={topMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{opt}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <div onClick={() => setOpenDropdown(o => o === 't2' ? null : 't2')} className="w-[124px] mx-1 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                           <span className="text-[12px] font-bold text-gray-700">{topMarket2}</span>
                           <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        {openDropdown === 't2' && (
                          <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                            <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                            {MARKET_OPTIONS.map(opt => (
                              <button key={opt} onClick={() => { setTopMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 flex items-center gap-2">
                                <div className="w-4">{topMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                                <span className={topMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{opt}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="w-[52px]" />
                    </div>

                    {/* Column label row */}
                    <div className="px-5 py-1 bg-[#f7f8f9] border-b border-gray-200 flex text-[11px] font-bold text-gray-400 items-center">
                      <div className="w-[56px]" />
                      <div className="flex-1" />
                      {renderMarketColHeader(topMarket1, "w-[192px]")}
                      {renderMarketColHeader(topMarket2, "w-[124px]")}
                      <div className="w-[52px]" />
                    </div>

                    {/* Tournaments + Matches (First few) */}
                    {Object.entries(
                      filteredPre.slice(0, 4).reduce((acc, ev) => {
                        const t = ev.event.tournament_name;
                        if (!acc[t]) acc[t] = [];
                        acc[t].push(ev);
                        return acc;
                      }, {} as Record<string, EventSnapshot[]>)
                    ).map(([tournament, events]) => (
                      <React.Fragment key={tournament}>
                        <div className="px-5 py-2 bg-[#f7f8f9] border-t border-b border-gray-200 flex items-center gap-2 mt-[-1px]">
                          <span className="text-[12px] font-bold text-gray-900">{tournament}</span>
                          <svg className="w-3 h-3 text-gray-400 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M5 9h4v11H5zm6-5h4v16h-4zm6 8h4v8h-4z"/></svg>
                        </div>
                        {events.map((ev) => {
                          const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
                          
                          const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
                          const d = starts ? new Date(starts) : null;
                          const now = new Date();
                          const tomorrow = new Date(now);
                          tomorrow.setDate(now.getDate() + 1);
                          let dayLabel = d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
                          if (d?.toDateString() === now.toDateString()) dayLabel = 'Heute';
                          else if (d?.toDateString() === tomorrow.toDateString()) dayLabel = 'Morgen';
                          const timeStr = d ? formatTime(d.toISOString()) : '';

                          return (
                            <div key={ev.event_id} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
                              <div className="w-[56px] flex-shrink-0 cursor-pointer pt-0.5 text-center items-start justify-start text-left" onClick={() => setSelectedEvent(ev)}>
                                <div className="text-[11px] font-medium text-gray-500 mb-0.5">{dayLabel}</div>
                                <div className="text-[13px] font-medium text-gray-500">{timeStr}</div>
                              </div>

                              <div className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer border-r border-gray-100 pr-4 pl-2" gap-1 onClick={() => setSelectedEvent(ev)}>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-[14px]">👕</span>
                                  <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[14px]">👕</span>
                                  <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
                                </div>
                              </div>

                              {renderMarketColCells(ev, topMarket1, "w-[192px]")}
                              {renderMarketColCells(ev, topMarket2, "w-[124px]")}

                              <button onClick={() => setSelectedEvent(ev)} className="w-[52px] flex items-center justify-end text-[12px] font-medium text-gray-500 hover:text-gray-800 transition">
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                {total} ›
                              </button>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                    
                    <div className="bg-[#f7f8f9] border-t border-gray-200 px-5 py-3 flex justify-end">
                      <button className="text-[13px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center">
                        Alle <span className="text-lg leading-none ml-1">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── LAST MINUTE SECTION (HOME PAGE) ─────────────────────────────── */}
              {filteredPre.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-[18px] font-bold text-gray-900 mb-3 px-1">Last Minute</h2>
                  <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-200" style={{position: 'relative', zIndex: 9}}>
                    
                    {/* Sport Tabs plain text style */}
                    <div className="flex bg-white px-2 pt-1 border-b border-gray-200 overflow-x-auto scrollbar-hide">
                      {['Fußball', 'Basketball', 'Tennis', 'Eishockey', 'Baseball'].map((s) => {
                        const sEng = s === 'Fußball' ? 'Soccer' : s === 'Eishockey' ? 'Ice Hockey' : s;
                        return (
                          <button
                            key={s}
                            onClick={() => setActiveSport(sEng)}
                            className={`px-5 py-3 text-[14px] font-bold transition border-b-[3px] -mb-[1px] whitespace-nowrap ${
                              activeSport === sEng
                                ? 'border-[#cc0000] text-gray-900'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>

                    {/* Table Dropdowns Top */}
                    <div className="px-5 py-2 flex justify-end bg-[#f7f8f9] border-b border-gray-200">
                      <div className="relative">
                        <div onClick={() => setOpenDropdown(o => o === 'lm1' ? null : 'lm1')} className="w-[192px] mx-1 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                           <span className="text-[12px] font-bold text-gray-700">{lastMinMarket1}</span>
                           <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        {openDropdown === 'lm1' && (
                          <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                            <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                            {MARKET_OPTIONS.map(opt => (
                              <button key={opt} onClick={() => { setLastMinMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 flex items-center gap-2">
                                <div className="w-4">{lastMinMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                                <span className={lastMinMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{opt}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <div onClick={() => setOpenDropdown(o => o === 'lm2' ? null : 'lm2')} className="w-[124px] mx-1 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                           <span className="text-[12px] font-bold text-gray-700">{lastMinMarket2}</span>
                           <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        {openDropdown === 'lm2' && (
                          <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                            <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                            {MARKET_OPTIONS.map(opt => (
                              <button key={opt} onClick={() => { setLastMinMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 flex items-center gap-2">
                                <div className="w-4">{lastMinMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                                <span className={lastMinMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{opt}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="w-[52px]" />
                    </div>

                    {/* Column label row */}
                    <div className="px-5 py-1 bg-[#f7f8f9] border-b border-gray-200 flex text-[11px] font-bold text-gray-400 items-center">
                      <div className="w-[56px]" />
                      <div className="flex-1" />
                      {renderMarketColHeader(lastMinMarket1, "w-[192px]")}
                      {renderMarketColHeader(lastMinMarket2, "w-[124px]")}
                      <div className="w-[52px]" />
                    </div>

                    {/* Tournaments + Matches (Last Minute mock) */}
                    {Object.entries(
                      filteredPre.slice(2, 6).reduce((acc, ev) => {
                        const t = ev.event.tournament_name;
                        if (!acc[t]) acc[t] = [];
                        acc[t].push(ev);
                        return acc;
                      }, {} as Record<string, EventSnapshot[]>)
                    ).map(([tournament, events]) => (
                      <React.Fragment key={tournament}>
                        <div className="px-5 py-2 bg-[#f7f8f9] border-t border-b border-gray-200 flex items-center gap-2 mt-[-1px]">
                          <span className="text-[12px] font-bold text-gray-900">{tournament}</span>
                          <svg className="w-3 h-3 text-gray-400 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M5 9h4v11H5zm6-5h4v16h-4zm6 8h4v8h-4z"/></svg>
                        </div>
                        {events.map((ev) => {
                          const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
                          const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
                          const d = starts ? new Date(starts) : null;
                          const now = new Date();
                          const tomorrow = new Date(now);
                          tomorrow.setDate(now.getDate() + 1);
                          let dayLabel = d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
                          if (d?.toDateString() === now.toDateString()) dayLabel = 'Heute';
                          else if (d?.toDateString() === tomorrow.toDateString()) dayLabel = 'Morgen';
                          const timeStr = d ? formatTime(d.toISOString()) : '';

                          return (
                            <div key={`lm-${ev.event_id}`} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
                              <div className="w-[56px] flex-shrink-0 cursor-pointer pt-0.5 text-center items-start justify-start text-left" onClick={() => setSelectedEvent(ev)}>
                                <div className="text-[11px] font-medium text-gray-500 mb-0.5">{dayLabel}</div>
                                <div className="text-[13px] font-medium text-gray-500">{timeStr}</div>
                              </div>
                              <div className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer border-r border-gray-100 pr-4 pl-2" gap-1 onClick={() => setSelectedEvent(ev)}>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-[14px]">👕</span>
                                  <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[14px]">👕</span>
                                  <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
                                </div>
                              </div>
                              {renderMarketColCells(ev, lastMinMarket1, "w-[192px]")}
                              {renderMarketColCells(ev, lastMinMarket2, "w-[124px]")}
                              <button onClick={() => setSelectedEvent(ev)} className="w-[52px] flex items-center justify-end text-[12px] font-medium text-gray-500 hover:text-gray-800 transition">
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                {total} ›
                              </button>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                    <div className="bg-[#f7f8f9] border-t border-gray-200 px-5 py-3 flex justify-end">
                      <button className="text-[13px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center">
                        Alle <span className="text-lg leading-none ml-1">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── LIVE SECTION (HOME PAGE) ─────────────────────────────── */}
              {filteredLive.length > 0 && (
                <div>
                  <h2 className="text-[18px] font-bold text-gray-900 mb-3 px-1">Live</h2>

                  <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-200" style={{position: 'relative', zIndex: 8}}>
                    
                    {/* Sport Tabs plain text style */}
                    <div className="flex bg-white px-2 pt-1 border-b border-gray-200">
                      {['Fußball', 'Tennis', 'Darts', 'Baseball', 'Beach-Volleyball', 'Tischtennis'].map((s) => {
                        const sEng = s === 'Fußball' ? 'Soccer' : s;
                        return (
                          <button
                            key={s}
                            onClick={() => setActiveSport(sEng)}
                            className={`px-5 py-3 text-[14px] font-bold transition border-b-[3px] -mb-[1px] ${
                              activeSport === sEng
                                ? 'border-[#cc0000] text-gray-900'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>

                    {/* Table Dropdowns Top */}
                    <div className="px-5 py-2 flex justify-end bg-[#f7f8f9] border-b border-gray-200">
                      <div className="relative">
                        <div onClick={() => setOpenDropdown(o => o === 'l1' ? null : 'l1')} className="w-[192px] mx-1 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                           <span className="text-[12px] font-bold text-gray-700">{liveMarket1}</span>
                           <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        {openDropdown === 'l1' && (
                          <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                            <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                            {MARKET_OPTIONS.map(opt => (
                              <button key={opt} onClick={() => { setLiveMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 flex items-center gap-2">
                                <div className="w-4">{liveMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                                <span className={liveMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{opt}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <div onClick={() => setOpenDropdown(o => o === 'l2' ? null : 'l2')} className="w-[192px] mx-1 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                           <span className="text-[12px] font-bold text-gray-700">{liveMarket2}</span>
                           <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        {openDropdown === 'l2' && (
                          <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                            <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                            {[...MARKET_OPTIONS, 'Tipp Restzeit'].map(opt => (
                              <button key={opt} onClick={() => { setLiveMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 flex items-center gap-2">
                                <div className="w-4">{liveMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                                <span className={liveMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{opt}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="w-[52px]" />
                    </div>

                    {/* Column label row */}
                    <div className="px-5 py-1 bg-[#f7f8f9] border-b border-gray-200 flex text-[11px] font-bold text-gray-400 items-center">
                      <div className="w-[56px]" />
                      <div className="flex-1" />
                      {renderMarketColHeader(liveMarket1, "w-[192px]")}
                      {renderMarketColHeader(liveMarket2, "w-[192px]")}
                      <div className="w-[52px]" />
                    </div>

                    {/* Matches */}
                    {filteredLive.slice(0, 4).map((ev, idx) => {
                      const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
                      
                      const clockStr = formatClock(ev) || "HZ";
                      const homeScore = ev.status?.home_score ?? 0;
                      const awayScore = ev.status?.away_score ?? 0;

                      return (
                        <div key={ev.event_id} className="px-5 py-3 flex items-start gap-3 hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
                          
                          <div className="w-[56px] flex-shrink-0 cursor-pointer pt-0.5 text-left border-r border-gray-100 pr-2" onClick={() => setSelectedEvent(ev)}>
                            <div className="text-[13px] font-bold text-gray-900">{clockStr}</div>
                            <div className="text-[9px] font-bold text-[#cc0000] tracking-widest mt-0.5">LIVE</div>
                          </div>

                          <div className="flex-1 min-w-0 flex flex-col cursor-pointer border-r border-gray-100 pr-4 pl-2 pb-1 relative" onClick={() => setSelectedEvent(ev)}>
                            <div className="flex items-center justify-between mb-1.5 pt-0.5">
                              <div className="flex items-center gap-2">
                                <span className="text-[14px]">👕</span>
                                <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
                              </div>
                              <span className="text-[13px] font-medium text-[#0088cc]">{homeScore}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[14px]">👕</span>
                                <span className="text-[13px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
                              </div>
                              <span className="text-[13px] font-medium text-[#0088cc]">{awayScore}</span>
                            </div>
                            <div className="mt-3 text-[11px] text-gray-400">1. Halbzeit</div>
                          </div>

                          {/* Dynamic Odds Column 1 */}
                          {renderMarketColCells(ev, liveMarket1, "w-[192px]")}
                          
                          {/* Dynamic Odds Column 2 */}
                          {renderMarketColCells(ev, liveMarket2, "w-[192px]")}

                          <button onClick={() => setSelectedEvent(ev)} className="w-[52px] flex items-start justify-end pt-2 text-[12px] font-medium text-gray-500 hover:text-gray-800 transition">
                            <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                            {total} ›
                          </button>
                        </div>
                      );
                    })}
                    
                    <div className="bg-[#f7f8f9] border-t border-gray-200 px-5 py-3 flex justify-end">
                      <button className="text-[13px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center">
                        Alle <span className="text-lg leading-none ml-1">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
                </>
              )}
            </>
          )}
        </main>

        {selectedEvent && (
          <div className="w-[360px] flex-shrink-0 flex flex-col bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200 relative sticky top-[80px] h-[calc(100vh-100px)]">
            <button 
              onClick={() => setSelectedEvent(null)}
              className="absolute top-4 right-4 z-[60] bg-white rounded-full p-2 shadow-md hover:bg-gray-100 border border-gray-100 transition"
            >
              <svg className="w-4 h-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex-1 overflow-y-auto min-h-0">
              <MatchDetailPage 
                event={selectedEvent}
                onBack={() => setSelectedEvent(null)}
                bets={bets}
                onAddBet={onAddBet}
                onRemoveBet={onRemoveBet}
                onLoginClick={onLoginClick}
              />
            </div>
          </div>
        )}

        {rightSidebar}
      </div>
    </div>
  );
};

export default DesktopHomePage;
